import React, { useState, useEffect } from "react";
import styled from "styled-components";
import MainLayout from "../components/MainLayout";
import { useInventoryStore } from "../store/inventoryStore";
import { useClothesStore } from "../store/clothesStore";
import { useProfileStore } from "../store/profileStore";
import { useToast } from "../components/ToastContainer";
import { useApiCall } from "../hooks/useApiCall";
import { useGlobalLoading } from "../hooks/useGlobalLoading";
import usePageTitle from "../utils/usePageTitle";
import InventoryFilters from "../components/InventoryFilters";
import AddProductModal from "../components/AddProductModal";
import AddClothesModal from "../components/AddClothesModal";
import ImportModal from "../components/ImportModal";
import ImportSourceModal from "../components/ImportSourceModal";
import ExcelImportModal from "../components/ExcelImportModal";
import SneakersInventory from "./inventory/SneakersInventory";
import ClothesInventory from "./inventory/ClothesInventory";
import importIcon from "../assets/import_icon.svg";

// Стилизованные компоненты
const PageContainer = styled.div`
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 80px);
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
`;

const PageTitle = styled.h1`
  font-family: "Ubuntu", sans-serif;
  font-weight: 500;
  font-size: 24px;
  color: #ffffff;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 28px;
  }

  @media (min-width: 1024px) {
    font-size: 32px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;

  @media (min-width: 768px) {
    gap: 12px;
  }

  @media (min-width: 1024px) {
    gap: 16px;
  }
`;

const ActionButton = styled.button<{ $primary?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background-color: ${(props) => (props.$primary ? "#24BA7D" : "#1A2E2E")};
  border: ${(props) => (props.$primary ? "none" : "1px solid #2C4A4A")};
  border-radius: 8px;
  color: #ffffff;
  font-family: "Ubuntu", sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) => (props.$primary ? "#1FA76A" : "#2C4A4A")};
    transform: translateY(-1px);
  }

  @media (min-width: 768px) {
    padding: 12px 20px;
    font-size: 15px;
  }

  @media (min-width: 1024px) {
    padding: 14px 24px;
    font-size: 16px;
  }
`;

const ButtonText = styled.span`
  @media (max-width: 480px) {
    display: none;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  svg {
    width: 100%;
    height: 100%;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 6px 12px;
  border: 2px solid #609f94;
  border-radius: 8px;
  margin-bottom: 20px;

  @media (min-width: 768px) {
    padding: 7px 14px;
    margin-bottom: 24px;
  }

  @media (min-width: 1024px) {
    padding: 8px 16px;
    margin-bottom: 32px;
  }

  @media (min-width: 1440px) {
    padding: 10px 20px;
    margin-bottom: 40px;
  }

  @media (min-width: 1920px) {
    padding: 12px 24px;
    margin-bottom: 48px;
  }
`;

const SearchInput = styled.input`
  background: transparent;
  border: none;
  outline: none;
  width: 100%;
  font-family: "Ubuntu", sans-serif;
  font-size: 12px;
  color: #ffffff;

  @media (min-width: 768px) {
    font-size: 13px;
  }

  @media (min-width: 1024px) {
    font-size: 14px;
  }

  @media (min-width: 1440px) {
    font-size: 16px;
  }

  @media (min-width: 1920px) {
    font-size: 18px;
  }

  &::placeholder {
    color: #609f94;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 20px;
  width: fit-content;

  @media (min-width: 768px) {
    margin-bottom: 24px;
  }

  @media (min-width: 1024px) {
    margin-bottom: 32px;
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  background-color: transparent;
  color: ${(props) => (props.$active ? "#24BA7D" : "#609F94")};
  border: none;
  border-bottom: 2px solid ${(props) => (props.$active ? "#24BA7D" : "transparent")};
  padding: 8px 16px;
  font-family: "Ubuntu", sans-serif;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    color: ${(props) => (props.$active ? "#24BA7D" : "#FFFFFF")};
    border-bottom-color: ${(props) =>
      props.$active ? "#24BA7D" : "rgba(96, 159, 148, 0.5)"};
  }

  @media (min-width: 768px) {
    padding: 10px 20px;
    font-size: 15px;
  }

  @media (min-width: 1024px) {
    padding: 12px 24px;
    font-size: 16px;
  }
`;

// Иконки
const ImportIcon = () => (
  <IconWrapper>
    <img src={importIcon} alt="Import" />
  </IconWrapper>
);

const AddIcon = () => (
  <IconWrapper>
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke="#FFFFFF" strokeWidth="2"/>
      <path d="M12 8V16M8 12H16" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  </IconWrapper>
);

const ExportIcon = () => (
  <IconWrapper>
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
        stroke="#FFFFFF"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 2V8H20"
        stroke="#FFFFFF"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16 13H8"
        stroke="#FFFFFF"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16 17H8"
        stroke="#FFFFFF"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 9H9H8"
        stroke="#FFFFFF"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </IconWrapper>
);

const Inventory: React.FC = () => {
  usePageTitle("Инвентарь");

  // Stores
  const {
    groupedItems,
    isLoading,
    hasInitialized,
    fetchInventory,
    currentPage: storePage,
    itemsPerPage: storeItemsPerPage,
    searchInventory,
    isSearching,
    clearSearchResults,
  } = useInventoryStore();

  const {
    hasInitialized: clothesHasInitialized,
    isLoading: clothesIsLoading,
    fetchClothes,
  } = useClothesStore();

  const { accessStockroom, fetchProfile } = useProfileStore();
  const { showSuccess, showError } = useToast();
  const { executeApiCall } = useApiCall();
  const { addLoadingStep, removeLoadingStep } = useGlobalLoading();

  // Local state
  const [activeTab, setActiveTab] = useState<"sneakers" | "clothes">("sneakers");
  const [searchQuery, setSearchQuery] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addClothesModalOpen, setAddClothesModalOpen] = useState(false);
  const [importSourceModalOpen, setImportSourceModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [excelImportModalOpen, setExcelImportModalOpen] = useState(false);

  // Загрузка профиля
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Инициализация данных
  useEffect(() => {
    if (!hasInitialized && !isLoading) {
      fetchInventory(0, 5);
    }
  }, [hasInitialized, isLoading, fetchInventory]);

  useEffect(() => {
    if (!clothesHasInitialized && !clothesIsLoading) {
      fetchClothes(0, 5);
    }
  }, [clothesHasInitialized, clothesIsLoading, fetchClothes]);

  // Глобальный загрузчик
  useEffect(() => {
    if (isLoading && (!groupedItems || groupedItems.length === 0)) {
      addLoadingStep("inventory-loading", "Ищем пары на складе...", 5);
    } else {
      removeLoadingStep("inventory-loading");
    }
  }, [isLoading, groupedItems, addLoadingStep, removeLoadingStep]);

  const handleTabChange = (tab: "sneakers" | "clothes") => {
    setActiveTab(tab);
    setSearchQuery("");
    clearSearchResults();
  };

  const handleAddProduct = async (data: any) => {
    const { createItem } = useInventoryStore.getState();
    try {
      await createItem(data);
    } catch (error) {
      console.error("Ошибка при добавлении товара:", error);
    }
  };

  const handleImport = async (credentials: { login: string; password: string }) => {
    const { api } = await import("../api/api");
    const result = await executeApiCall(
      () => api.importFromWarehouse(credentials),
      {
        successMessage: "Импорт начат...",
        errorMessage: "Ошибка при импорте товаров",
      }
    );
    if (result !== null) {
      await fetchInventory(storePage * storeItemsPerPage, storeItemsPerPage);
    }
  };

  const handleExcelImport = async (file: File) => {
    const { api } = await import("../api/api");
    const result = await executeApiCall(() => api.importFromExcel(file), {
      successMessage: "Импорт начат...",
      errorMessage: "Ошибка при импорте товаров из Excel файла",
    });
    if (result !== null) {
      await fetchInventory(storePage * storeItemsPerPage, storeItemsPerPage);
    }
  };

  const handleExport = async () => {
    try {
      const { api } = await import("../api/api");
      addLoadingStep("export-loading", "Экспортируем данные...", 3);
      const blob = await api.exportToExcel();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const now = new Date();
      const dateStr = now.toISOString().split("T")[0];
      link.download = `inventory_export_${dateStr}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showSuccess("Файл успешно экспортирован");
    } catch (error) {
      console.error("Ошибка экспорта:", error);
      showError("Ошибка при экспорте данных");
    } finally {
      removeLoadingStep("export-loading");
    }
  };

  if (isLoading && (!groupedItems || groupedItems.length === 0)) {
    return (
      <MainLayout activePage="inventory">
        <div />
      </MainLayout>
    );
  }

  return (
    <MainLayout activePage="inventory">
      <PageContainer>
        <HeaderContainer data-tutorial="inventory-header">
          <PageTitle>Инвентарь</PageTitle>
          <HeaderActions>
            <ActionButton onClick={handleExport}>
              <ButtonText>Экспорт</ButtonText>
              <ExportIcon />
            </ActionButton>
            <ActionButton onClick={() => setImportSourceModalOpen(true)}>
              <ButtonText>Импортировать</ButtonText>
              <ImportIcon />
            </ActionButton>
            <ActionButton
              $primary
              onClick={() => {
                if (activeTab === "sneakers") {
                  setAddModalOpen(true);
                } else {
                  setAddClothesModalOpen(true);
                }
              }}
              data-tutorial="add-product-button"
            >
              <ButtonText>Добавить</ButtonText>
              <AddIcon />
            </ActionButton>
          </HeaderActions>
        </HeaderContainer>

        <SearchContainer>
          <SearchInput
            placeholder="Найти нужный товар"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {isSearching && (
            <div style={{ marginLeft: "10px", fontSize: "14px", color: "#609F94" }}>
              Поиск...
            </div>
          )}
        </SearchContainer>

        <InventoryFilters />

        <TabsContainer>
          <Tab $active={activeTab === "sneakers"} onClick={() => handleTabChange("sneakers")}>
            Обувь
          </Tab>
          <Tab $active={activeTab === "clothes"} onClick={() => handleTabChange("clothes")}>
            Одежда
          </Tab>
        </TabsContainer>

        <div data-tutorial="products-grid">
          {activeTab === "sneakers" ? (
            <SneakersInventory
              searchQuery={searchQuery}
              onAddProduct={() => setAddModalOpen(true)}
            />
          ) : (
            <ClothesInventory
              searchQuery={searchQuery}
              onAddClothes={() => setAddClothesModalOpen(true)}
            />
          )}
        </div>

        {/* Modals */}
        <AddProductModal
          isOpen={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onAdd={handleAddProduct}
        />

        <AddClothesModal
          isOpen={addClothesModalOpen}
          onClose={() => setAddClothesModalOpen(false)}
        />

        <ImportSourceModal
          isOpen={importSourceModalOpen}
          onClose={() => setImportSourceModalOpen(false)}
          onSelectMyStore={() => {
            setImportSourceModalOpen(false);
            setImportModalOpen(true);
          }}
          onSelectExcel={() => {
            setImportSourceModalOpen(false);
            setExcelImportModalOpen(true);
          }}
        />

        <ImportModal
          isOpen={importModalOpen}
          onClose={() => setImportModalOpen(false)}
          onImport={handleImport}
        />

        <ExcelImportModal
          isOpen={excelImportModalOpen}
          onClose={() => setExcelImportModalOpen(false)}
          onImport={handleExcelImport}
        />
      </PageContainer>
    </MainLayout>
  );
};

export default Inventory;
